/**
 * Variante achetable : en stock, ou épuisée mais vente autorisée (précommande / continue).
 * @param {{ available?: boolean, inventory_policy?: string, inventory_quantity?: number }} variant
 * @returns {boolean}
 */
function deployerVariantIsPurchasable(variant) {
    if (!variant) return false;
    if (variant.available) return true;
    return variant.inventory_policy === 'continue';
}

class DeployerQuantityBreaks extends HTMLElement {
    constructor() {
        super();
        // Keep broader selectors from file #1
        this.main_product_form =
            document.querySelector('.product-form form[action*="/cart/add"]') ||
            document.querySelector('.product__page form[action*="/cart/add"]') ||
            document.querySelector('.product form[action*="/cart/add"]') ||
            document.querySelector('form[action*="/cart/add"]');

        this.main_product_section_id = this.dataset.sectionId;
        this.show_variant_picker = this.getAttribute('data-show-variant-picker') == 'true' ? true : false;
        this.variantData = variantData;
        this.isInitializing = true; // Flag pour distinguer l'initialisation des changements ultérieurs
        if (this.main_product_form === null) return console.error('Deployer: No product form found');

        // update when variant changes (works with Broadcast, Prestige, Dawn & co, Impulse)
        if (this.main_product_form.querySelector('input[name="id"]') !== null) {
            this.main_product_form.querySelector('input[name="id"]').addEventListener('change', (event) => {
                this.renderDeployerQuantityBreaks(event.target.value);
            });
        } else {
            if (typeof subscribe === 'function') {
                this.variantChangeSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                    this.renderDeployerQuantityBreaks(event.data.variant.id);
                });
            } else {
                document.addEventListener('variant:change', (event) => {
                    this.renderDeployerQuantityBreaks(event.detail.variant.id);
                });
            }
        }
    }

    connectedCallback() {
        // Clear any existing items inputs on initialization (from file #2)
        this.clearFormInputs();

        if (this.show_variant_picker === false) {
            // Simple mode: initialize inputs and price immediately
            this.updateFormInputs(this.getSelectedQuantity());
            this.updatePrice(this.getSelectedQuantity());
            this.updateAddButtonState();
            this.isInitializing = false; // Réinitialiser le flag après l'initialisation
        } else {
            // Variant picker mode: wait a bit for pickers to init, then compute all prices
            this.updateFormInputs(this.getSelectedQuantity());
            setTimeout(() => {
                this.initializeAllQuantityBreakPrices();
                this.updatePrice(this.getSelectedQuantity());
                this.updateAddButtonState();
                this.isInitializing = false; // Réinitialiser le flag après l'initialisation
            }, 300);
        }

        // update when quantity changes
        if (this.show_variant_picker === false) {
            this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
                quantity_break.addEventListener('click', () => {
                    this.selectQuantity(quantity_break);
                    this.updateFormInputs(quantity_break);
                    this.updateImages(quantity_break);
                });
            });
        } else {
            const variantPickers = this.querySelectorAll('deployer-quantity-break-variant-picker');

            variantPickers.forEach((variantPicker) => {
                variantPicker.addEventListener('variantChange', (event) => {
                    // Sync the picker dataset.id with the selected variant
                    const newVariantId = event.detail.variant.id;
                    variantPicker.dataset.id = newVariantId;
                    // Update form inputs with new variant selection
                    this.updateFormInputs(this.getSelectedQuantity());
                    // Recompute price and button state
                    const selected = this.getSelectedQuantity();
                    if (selected) this.updatePrice(selected);
                    this.updateAddButtonState();
                });
            });

            // Listen to additional product variant changes
            const additionalVariantPickers = this.querySelectorAll('deployer-quantity-break-additional-variant-picker');
            additionalVariantPickers.forEach((variantPicker) => {
                variantPicker.addEventListener('variantChange', (event) => {
                    // Update form inputs and price when additional product variant changes
                    this.updateFormInputs(this.getSelectedQuantity());
                    const selected = this.getSelectedQuantity();
                    if (selected) this.updatePrice(selected);
                    this.updateAddButtonState();
                });
            });

            // update when variant changes in quantity break
            this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
                quantity_break.addEventListener('click', (event) => {
                    this.selectQuantity(quantity_break);
                    if (event.target.type != 'select-one')
                        this.updateImages(quantity_break);
                    setTimeout(() => {
                        this.updateFormInputs(quantity_break);
                        this.updatePrice(quantity_break);
                        this.updateAddButtonState();
                    }, 100);
                });
            });
        }
    }

    updateImages(element) {
        const productContainer = this.closest('.product-section');
        const variantId = element.dataset.variantId;
        const variant = this.variantData.filter((variant) => {
            return variant.id == variantId;
        });
        
        // Ne pas déclencher l'événement variantImageChange lors de l'initialisation
        // pour éviter d'afficher l'image de variante au lieu de la première image produit
        if (this.isInitializing) {
            return;
        }
        
        productContainer.dispatchEvent(new CustomEvent('variantImageChange', {
            detail: {
                variant: variant[0]
            }
        }));
    }

    renderDeployerQuantityBreaks(variant_id) {
        this.classList.add('loading');
        var variant_param = '?variant=' + variant_id + '&';
        if (variant_id === undefined) variant_param = '?';

        fetch(window.location.pathname + variant_param + 'section_id=' + this.main_product_section_id)
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');

                const deployerQuantityBreaksSource = html.querySelector('deployer-quantity-breaks');
                const deployerQuantityBreaksDestination = document.querySelector('deployer-quantity-breaks');

                if (deployerQuantityBreaksSource && deployerQuantityBreaksDestination) {
                    deployerQuantityBreaksDestination.innerHTML = deployerQuantityBreaksSource.innerHTML;
                    deployerQuantityBreaksDestination.connectedCallback();
                }
            })
            .then(() => {
                this.classList.remove('loading');
            });
    }

    selectQuantity(quantity_break) {
        if (quantity_break.classList.contains('disabled-quantity-break')) return;
        this.resetSelectedQuantity();
        // In picker mode, recompute price when selecting a different bundle
        if (this.show_variant_picker) {
            this.updatePrice(quantity_break);
        }
        quantity_break.classList.add('selected-quantity-break');
    }

    resetSelectedQuantity() {
        this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
            quantity_break.classList.remove('selected-quantity-break');
        });
    }

    updateFormInputs(quantity_break) {
        if (!quantity_break) return;
        // Clear all existing items inputs first (from file #2)
        this.clearFormInputs();

        // Also clear any inputs that might have been created by other scripts
        const allItemsInputs = this.main_product_form.querySelectorAll('input[name*="items"]');
        if (allItemsInputs.length > 0) {
            allItemsInputs.forEach((input) => input.remove());
        }

        if (this.show_variant_picker === false) {
            const quantityInput = this.main_product_form.querySelector('input[name="quantity"]');
            const quantityValue = quantity_break.getAttribute('data-quantity');

            if (quantityInput) {
                quantityInput.value = quantityValue;
            } else {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'quantity';
                input.value = quantityValue;
                this.main_product_form.appendChild(input);
            }

            // Ajouter le produit supplémentaire si présent
            this.addAdditionalProductInputs(quantity_break, 0);
        } else {
            const variantsPickers = quantity_break.querySelectorAll('deployer-quantity-break-variant-picker');
            if (variantsPickers.length > 0) {
                // Clear existing variant inputs first
                this.clearVariantInputs();

                variantsPickers.forEach((variantsPicker, index) => {
                    const variantId = variantsPicker.dataset.id;

                    if (index === 0) {
                        // Update the main form variant selection. Support both select[id] or hidden input fallbacks.
                        const selectElement = this.main_product_form.querySelector('select[name="id"]');
                        if (selectElement) {
                            Array.from(selectElement.options).forEach(option => {
                                option.selected = option.value === variantId;
                            });
                        } else {
                            // support theme differences: input[name="product-id"] (file #1) or input[name="id"] (file #2)
                            const mainVariantInput = this.main_product_form.querySelector('input[name="product-id"]') || this.main_product_form.querySelector('input[name="id"]');
                            if (mainVariantInput) {
                                mainVariantInput.value = variantId;
                            }
                        }
                        // Do NOT create items[0] for the first variant
                    } else {
                        // Additional variants => create items[*][id] and items[*][quantity]
                        const idx = index - 1;
                        const idInput = document.createElement('input');
                        idInput.type = 'hidden';
                        idInput.name = `items[${idx}][id]`;
                        idInput.value = variantId;
                        this.main_product_form.appendChild(idInput);

                        const qtyInput = document.createElement('input');
                        qtyInput.type = 'hidden';
                        qtyInput.name = `items[${idx}][quantity]`;
                        qtyInput.value = '1';
                        this.main_product_form.appendChild(qtyInput);
                    }
                });

                // Ajouter le produit supplémentaire après tous les variants principaux
                const lastIndex = variantsPickers.length - 1;
                this.addAdditionalProductInputs(quantity_break, lastIndex);
            }
        }
    }

    addAdditionalProductInputs(quantity_break, startIndex) {
        const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;

        if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0) {
            // Récupérer la variante sélectionnée du variant picker
            const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
            const additionalProductId = additionalVariantPicker 
                ? additionalVariantPicker.dataset.id 
                : quantity_break.dataset.additionalProductId;

            if (additionalProductId) {
                const idInput = document.createElement('input');
                idInput.type = 'hidden';
                idInput.name = `items[${startIndex}][id]`;
                idInput.value = additionalProductId;
                this.main_product_form.appendChild(idInput);

                const qtyInput = document.createElement('input');
                qtyInput.type = 'hidden';
                qtyInput.name = `items[${startIndex}][quantity]`;
                qtyInput.value = additionalProductQuantity;
                this.main_product_form.appendChild(qtyInput);
            }
        }
    }

    clearFormInputs() {
        const inputs = this.main_product_form.querySelectorAll('input[name^="items"]');
        inputs.forEach((input) => input.remove());
        // Double-check
        const remaining = this.main_product_form.querySelectorAll('input[name^="items"]');
        if (remaining.length > 0) remaining.forEach((i) => i.remove());
    }

    clearVariantInputs() {
        this.clearFormInputs();
    }

    getSelectedQuantity() {
        return this.querySelector('.selected-quantity-break');
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.main_product_form;
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text) addButtonText.textContent = text;
        } else {
            addButton.removeAttribute('disabled');
            if (text) addButtonText.textContent = text;
        }
        if (!modifyClass) return;
    }

    // ===== Added from File #2: smarter button state =====
    updateAddButtonState() {
        const selectedQuantityBreak = this.getSelectedQuantity();
        if (!selectedQuantityBreak) return;

        if (this.show_variant_picker) {
            const variantsSelectedAvailable = Array.from(selectedQuantityBreak.querySelectorAll('deployer-quantity-break-variant-picker'))
                .every((fieldset) => fieldset.dataset.available === 'true');

            const variantsSelectedPreorder = Array.from(selectedQuantityBreak.querySelectorAll('deployer-quantity-break-variant-picker'))
                .some((fieldset) => fieldset.dataset.preorder === 'true');

            if (variantsSelectedAvailable || variantsSelectedPreorder) {
                const defaultText = this.main_product_form.querySelector('[name="add"] > span')?.dataset.defaultText || 'Ajouter au panier';
                this.toggleAddButton(false, defaultText, false);
            } else {
                this.toggleAddButton(true, theme.strings?.soldOut || 'Sold out', false);
            }
        } else {
            const variantId = selectedQuantityBreak.dataset.variantId;
            const variant = this.variantData.find(v => v.id == variantId);
            if (variant && (variant.available || (variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue'))) {
                const defaultText = this.main_product_form.querySelector('[name="add"] > span')?.dataset.defaultText || 'Ajouter au panier';
                this.toggleAddButton(false, defaultText, false);
            } else {
                this.toggleAddButton(true, theme.strings?.soldOut || 'Épuisé', false);
            }
        }
    }

    // ===== Replaced pricing with advanced logic from File #2 =====
    updatePrice(quantity_break) {
        const productPriceContainer = document.querySelector('[data-product-price].product__price');
        if (!productPriceContainer) return;

        if (this.show_variant_picker === false) {
            // Simple mode: recalculer le prix avec le produit supplémentaire
            const quantityBreakPriceElement = quantity_break.querySelector('.quantity-break_price-container_price');
            const quantityBreakComparePriceElement = quantity_break.querySelector('.quantity-break_price-container_compare-at-price');

            let basePrice = 0;
            let baseComparePrice = 0;

            if (quantityBreakPriceElement) {
                // Extraire le prix numérique
                const priceText = quantityBreakPriceElement.textContent.trim();
                basePrice = this.parsePrice(priceText);
            }
            if (quantityBreakComparePriceElement) {
                const comparePriceText = quantityBreakComparePriceElement.textContent.trim();
                baseComparePrice = this.parsePrice(comparePriceText);
            }

            // Ajouter le produit supplémentaire
            const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;
            if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0) {
                // Récupérer la variante sélectionnée du variant picker
                const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
                let price = 0;
                
                if (additionalVariantPicker) {
                    const additionalVariantId = additionalVariantPicker.dataset.id;
                    const quantityBreakIndex = Array.from(document.querySelectorAll('.quantity-break')).indexOf(quantity_break) + 1;
                    const additionalVariantData = additionalProductsData[quantityBreakIndex.toString()] || [];
                    const additionalVariant = additionalVariantData.find(v => v.id == additionalVariantId);
                    
                    if (additionalVariant) {
                        price = parseFloat(additionalVariant.price) / 100;
                    }
                } else {
                    // Fallback sur le prix par défaut
                    const additionalProductPrice = quantity_break.dataset.additionalProductPrice;
                    if (additionalProductPrice) {
                        price = parseFloat(additionalProductPrice) / 100;
                    }
                }
                
                const quantity = parseInt(additionalProductQuantity);
                basePrice += price * quantity;
                baseComparePrice += price * quantity;
            }

            const priceSpan = productPriceContainer.querySelector('span:not([data-product-compare-price])');
            if (priceSpan) {
                priceSpan.innerHTML = this.formatPrice(basePrice);
            }

            if (baseComparePrice > basePrice) {
                let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
                if (!comparePriceSpan) {
                    comparePriceSpan = document.createElement('span');
                    comparePriceSpan.setAttribute('data-product-compare-price', '');
                    productPriceContainer.appendChild(comparePriceSpan);
                }
                comparePriceSpan.innerHTML = this.formatPrice(baseComparePrice);
            } else {
                const comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
                if (comparePriceSpan) comparePriceSpan.remove();
            }
        } else {
            // Variant picker mode: compute total with discounts
            this.updatePriceForSelectedVariants(quantity_break, productPriceContainer);
        }
    }

    parsePrice(priceText) {
        // Parse price from formatted text (e.g., "29,90 €" or "29.90 €")
        const cleaned = priceText.replace(/[^\d,.-]/g, '').replace(',', '.');
        return parseFloat(cleaned) || 0;
    }

    initializeAllQuantityBreakPrices() {
        // Initialize price blocks for all quantity breaks to avoid 0€
        this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
            this.updatePriceForSelectedVariants(quantity_break, null);
        });
    }

    updatePriceForSelectedVariants(quantity_break, productPriceContainer) {
        const variantsPickers = quantity_break.querySelectorAll('deployer-quantity-break-variant-picker');
        if (variantsPickers.length === 0) return;

        const isInitialization = !productPriceContainer;

        // Discount params set on the quantity-break element via data attributes
        const discountValue = quantity_break.dataset.discountValue;
        const discountPercentage = quantity_break.dataset.discountPercentage;
        const discountOnTotal = quantity_break.dataset.discountOnTotal === 'true';

        const variantsWithPrices = [];
        variantsPickers.forEach((variantPicker) => {
            const variantId = variantPicker.dataset.id;
            const variant = this.variantData.find(v => v.id == variantId);
            if (variant) {
                const price = parseFloat(variant.price) / 100; // Shopify prices are in cents
                const comparePrice = variant.compare_at_price ? parseFloat(variant.compare_at_price) / 100 : price;
                variantsWithPrices.push({ variant, price, comparePrice });
            }
        });

        // Ajouter le produit supplémentaire au calcul du prix
        const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;
        if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0) {
            // Récupérer la variante sélectionnée du variant picker
            const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
            if (additionalVariantPicker) {
                const additionalVariantId = additionalVariantPicker.dataset.id;
                const quantityBreakIndex = Array.from(document.querySelectorAll('.quantity-break')).indexOf(quantity_break) + 1;
                const additionalVariantData = additionalProductsData[quantityBreakIndex.toString()] || [];
                const additionalVariant = additionalVariantData.find(v => v.id == additionalVariantId);
                
                if (additionalVariant) {
                    const price = parseFloat(additionalVariant.price) / 100;
                    const comparePrice = additionalVariant.compare_at_price ? parseFloat(additionalVariant.compare_at_price) / 100 : price;
                    const quantity = parseInt(additionalProductQuantity);
                    for (let i = 0; i < quantity; i++) {
                        variantsWithPrices.push({ variant: additionalVariant, price: price, comparePrice: comparePrice });
                    }
                }
            }
        }

        let finalPrice = 0;
        let finalComparePrice = 0;

        if (variantsWithPrices.length > 0) {
            if (discountOnTotal) {
                // Logic 1: discount applied on total
                const totalPrice = variantsWithPrices.reduce((s, v) => s + v.price, 0);
                const totalCompare = variantsWithPrices.reduce((s, v) => s + v.comparePrice, 0);
                finalPrice = totalPrice;
                finalComparePrice = totalCompare;

                if (discountValue && discountValue !== '') {
                    const amount = parseFloat(String(discountValue).replace(',', '.'));
                    finalPrice = Math.max(0, totalPrice - amount);
                } else if (discountPercentage && discountPercentage !== '') {
                    const percent = parseFloat(String(discountPercentage).replace(',', '.'));
                    finalPrice = totalPrice - (totalPrice * percent) / 100;
                }
            } else {
                // Logic 2: discount on the cheapest variant
                variantsWithPrices.sort((a, b) => a.price - b.price);
                const lowest = variantsWithPrices[0];
                const others = variantsWithPrices.slice(1);

                let lowestPriceWithDiscount = lowest.price;
                let lowestCompareWithDiscount = lowest.comparePrice;

                if (discountValue && discountValue !== '') {
                    const amount = parseFloat(String(discountValue).replace(',', '.'));
                    lowestPriceWithDiscount = Math.max(0, lowest.price - amount);
                } else if (discountPercentage && discountPercentage !== '') {
                    const percent = parseFloat(String(discountPercentage).replace(',', '.'));
                    lowestPriceWithDiscount = lowest.price * (1 - percent / 100);
                }

                finalPrice = lowestPriceWithDiscount + others.reduce((s, v) => s + v.price, 0);
                finalComparePrice = lowestCompareWithDiscount + others.reduce((s, v) => s + v.comparePrice, 0);
            }
        }

        if (finalPrice <= 0) finalPrice = finalComparePrice;
        if (finalComparePrice <= 0) {
            finalComparePrice = variantsWithPrices.reduce((s, v) => s + v.comparePrice, 0);
        }

        // Update price inside the quantity-break card
        this.updateQuantityBreakPrice(quantity_break, finalPrice, finalComparePrice);

        // Update main product price if not initialization run
        if (!isInitialization && productPriceContainer) {
            this.updatePriceDisplay(productPriceContainer, finalPrice, finalComparePrice);
        }
    }

    updateQuantityBreakPrice(quantity_break, totalPrice, totalComparePrice) {
        const priceElement = quantity_break.querySelector('.quantity-break_price-container_price');
        if (priceElement) {
            priceElement.innerHTML = this.formatPrice(totalPrice);
        }

        const comparePriceElement = quantity_break.querySelector('.quantity-break_price-container_compare-at-price');
        if (comparePriceElement) {
            if (totalComparePrice > totalPrice) {
                comparePriceElement.innerHTML = this.formatPrice(totalComparePrice);
                comparePriceElement.style.display = 'block';
            } else {
                comparePriceElement.style.display = 'none';
            }
        }

        const savingsElement = quantity_break.querySelector('.quantity-break_price-container_savings');
        if (savingsElement && totalComparePrice > totalPrice) {
            const savings = totalComparePrice - totalPrice;
            const originalSavingsText = savingsElement.dataset.originalText;
            if (originalSavingsText) {
                const updatedSavingsText = originalSavingsText.replace('[savings]', this.formatPrice(savings));
                savingsElement.textContent = updatedSavingsText;
                savingsElement.style.display = 'block';
            }
        } else if (savingsElement) {
            savingsElement.style.display = 'none';
        }
    }

    updatePriceDisplay(productPriceContainer, totalPrice, totalComparePrice) {
        let priceSpan = productPriceContainer.querySelector('span:not([data-product-compare-price])');
        if (!priceSpan) {
            priceSpan = document.createElement('span');
            productPriceContainer.appendChild(priceSpan);
        }
        priceSpan.innerHTML = this.formatPrice(totalPrice);

        if (totalComparePrice > totalPrice) {
            let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
            if (!comparePriceSpan) {
                comparePriceSpan = document.createElement('span');
                comparePriceSpan.setAttribute('data-product-compare-price', '');
                productPriceContainer.appendChild(comparePriceSpan);
            }
            comparePriceSpan.innerHTML = this.formatPrice(totalComparePrice);
        } else {
            const comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
            if (comparePriceSpan) comparePriceSpan.remove();
        }
    }

    updateComparePrice(productPriceContainer, quantityBreakComparePriceElement) {
        let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
        if (quantityBreakComparePriceElement) {
            if (comparePriceSpan) {
                comparePriceSpan.innerHTML = quantityBreakComparePriceElement.innerHTML;
            } else {
                comparePriceSpan = document.createElement('span');
                comparePriceSpan.setAttribute('data-product-compare-price', '');
                comparePriceSpan.innerHTML = quantityBreakComparePriceElement.innerHTML;
                productPriceContainer.appendChild(comparePriceSpan);
            }
        } else if (comparePriceSpan) {
            comparePriceSpan.remove();
        }
    }

    formatPrice(price) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    }
}

customElements.define('deployer-quantity-breaks', DeployerQuantityBreaks);

class QuantityBreakVariantPicker extends HTMLElement {
    constructor() {
        super();
        this.DeployerQuantityBreaks = document.querySelector('deployer-quantity-breaks');
        this.inputs = this.querySelectorAll('input[type="radio"]');
        this.selects = this.querySelectorAll('select');
        this.optionsNames = this.querySelectorAll('legend span');
        this.options = [];
        this.currentVariant = this.dataset.id; // as in file #1
        this.variantData = variantData;
        this.isInitializing = true; // Flag pour distinguer l'initialisation des changements ultérieurs
    }

    connectedCallback() {
        this.updateOptions();
        this.updateVariantStatuses();
        this.initializeSelects();

        this.inputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
        this.selects.forEach((select) => {
            select.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });

        // Ensure initial price refresh (from file #2)
        setTimeout(() => {
            const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
            if (deployerQuantityBreaks) {
                const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
                if (selectedQuantityBreak) {
                    deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                }
            }
        }, 100);
    }

    initializeSelects() {
        // Récupérer la variante par défaut depuis le dataset
        const defaultVariantId = this.dataset.id;
        const defaultVariant = this.variantData.find(v => v.id == defaultVariantId);
        
        if (defaultVariant) {
            // Mettre à jour tous les selects avec les options de la variante par défaut
            this.selects.forEach((select) => {
                const optionPosition = select.closest('fieldset').dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue) {
                        select.value = optionValue;
                    }
                }
            });
            
            // Mettre à jour tous les inputs radio avec les options de la variante par défaut
            this.inputs.forEach((input) => {
                const fieldset = input.closest('fieldset');
                const optionPosition = fieldset.dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue && input.value === optionValue) {
                        input.checked = true;
                    }
                }
            });
            
            // Déclencher onChange pour mettre à jour l'état
            this.onChange();
        }
    }

    onChange() {
        this.updateOptions();
        this.updateDataId();
        this.updateOptionsNames();
        this.updateVariantStatuses();
        this.updateImages();

        // Ne pas déclencher l'événement variantImageChange lors de l'initialisation
        // pour éviter d'afficher l'image de variante au lieu de la première image produit
        if (this.isInitializing) {
            this.isInitializing = false; // Réinitialiser le flag après la première exécution
            return;
        }

        const productContainer = this.closest('.product-section');
        if (productContainer) {
            productContainer.dispatchEvent(new CustomEvent('variantImageChange', {
                detail: {
                    variant: this.currentVariant
                }
            }));
        }

        // Trigger price recomputation on parent
        const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        if (deployerQuantityBreaks) {
            const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
            if (selectedQuantityBreak) {
                deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                deployerQuantityBreaks.updateAddButtonState();
            }
        }

        this.dispatchEvent(new CustomEvent('variantChange', {
            detail: {
                variant: this.currentVariant
            }
        }));
    }

    updateOptions() {
        this.options = [];
        const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
        fieldsets.sort((a, b) => {
            const posA = parseInt(a.dataset.optionPosition, 10) || 0;
            const posB = parseInt(b.dataset.optionPosition, 10) || 0;
            return posA - posB;
        });
        const maxPosition = fieldsets.reduce((max, fs) => {
            const p = parseInt(fs.dataset.optionPosition, 10) || 0;
            return Math.max(max, p);
        }, 0);
        for (let pos = 1; pos <= maxPosition; pos++) {
            const fieldset = fieldsets.find((fs) => parseInt(fs.dataset.optionPosition, 10) === pos);
            if (!fieldset) continue;
            const selectEl = fieldset.querySelector('select');
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (selectEl) {
                this.options.push(selectEl.value);
            } else if (checkedRadio) {
                this.options.push(checkedRadio.value);
            }
        }
    }

    updateDataId() {
        const matched = this.variantData.find((variant) => {
            if (!variant.options || variant.options.length !== this.options.length) return false;
            return !variant.options
                .map((option, index) => {
                    return this.options[index] === option;
                })
                .includes(false);
        });
        if (matched) {
            this.currentVariant = matched;
        } else {
            const fallback = this.variantData.find((v) => String(v.id) === String(this.dataset.id));
            if (fallback) this.currentVariant = fallback;
        }
        if (!this.currentVariant) return;

        this.dataset.id = String(this.currentVariant.id);

        this.dataset.available = deployerVariantIsPurchasable(this.currentVariant) ? 'true' : 'false';

        const preorder =
            this.currentVariant.inventory_quantity <= 0 && this.currentVariant.inventory_policy === 'continue';
        this.dataset.preorder = preorder ? 'true' : 'false';
    }

    updateVariantStatuses() {
        const selectedOptions = this.querySelectorAll(':checked');
        const inputWrappers = [...this.querySelectorAll('fieldset')];
        let selectedOptionOneVariants = null;
        let selectedOptionTwoVariants = null;
        let selectedOptionThreeVariants = null;
        if (selectedOptions[0]) {
            selectedOptionOneVariants = this.variantData.filter((variant) => selectedOptions[0].value === variant.options[0]);
        }
        if (selectedOptions[1]) {
            selectedOptionTwoVariants = this.variantData.filter((variant) => selectedOptions[1].value === variant.options[1]);
        }
        if (selectedOptions[2]) {
            selectedOptionThreeVariants = this.variantData.filter((variant) => selectedOptions[2].value === variant.options[2]);
        }
        inputWrappers.forEach((option, index) => {
            if (selectedOptions.length <= 1) return;
            if (index === 0) {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index + 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionTwoVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index + 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            } else {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index - 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            }
        });
    }

    setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
            const value = element.getAttribute('value');
            const availableElement = availableValuesList.includes(value);

            if (element.tagName === 'INPUT') {
                if (availableElement) {
                    element.classList.remove('disabled');
                } else {
                    element.classList.add('disabled');
                }
            } else if (element.tagName === 'OPTION') {
                if (availableElement) {
                    //element.disabled = false;
                    element.classList.remove('disabled');
                } else {
                    //element.disabled = true;
                    element.classList.add('disabled');
                }
            }
        });
    }

    updateImages() {
        const variant = this.variantData.filter((variant) => {
            return variant.id === this.currentVariant.id;
        });

        const transparentImage = this.getTransparentImage(this.currentVariant.id);

        if (transparentImage) {
            this.setImage(transparentImage);
        } else if (variant[0]?.featured_image?.src) {
            this.setImage(variant[0].featured_image.src);
        }
    }

    /**
     * Set image
     */
    setImage(imageUrl) {
        const quantityBreakMainContent = this.closest('.quantity-break_main-content');
        const images = quantityBreakMainContent.querySelectorAll('img');
        const index = Number.parseInt(this.dataset.index) - 1;
        const image = images[index];

        image.src = imageUrl;
    }

    /**
     * Get the transparent image from the variant id
     * @returns string|null
     */
    getTransparentImage(variantId) {
        const transparentImage = transparentImages.find((variant) => variant.id === variantId);
        return transparentImage?.transparentImage;
    }

    updateOptionsNames() {
        this.optionsNames.forEach((optionName, index) => {
            optionName.innerText = this.options[index];
        });
    }
}
customElements.define('deployer-quantity-break-variant-picker', QuantityBreakVariantPicker);

class QuantityBreakAdditionalVariantPicker extends HTMLElement {
    constructor() {
        super();
        this.DeployerQuantityBreaks = document.querySelector('deployer-quantity-breaks');
        this.inputs = this.querySelectorAll('input[type="radio"]');
        this.selects = this.querySelectorAll('select');
        this.optionsNames = this.querySelectorAll('legend span');
        this.options = [];
        this.currentVariant = this.dataset.id;
        
        // Trouver l'index de la quantity break
        const quantityBreak = this.closest('.quantity-break');
        const quantityBreakIndex = Array.from(document.querySelectorAll('.quantity-break')).indexOf(quantityBreak) + 1;
        this.variantData = additionalProductsData[quantityBreakIndex.toString()] || [];
    }

    connectedCallback() {
        this.updateOptions();
        this.updateVariantStatuses();
        this.initializeSelects();

        this.inputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
        this.selects.forEach((select) => {
            select.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
    }

    initializeSelects() {
        const defaultVariantId = this.dataset.id;
        const defaultVariant = this.variantData.find(v => v.id == defaultVariantId);
        
        if (defaultVariant) {
            this.selects.forEach((select) => {
                const optionPosition = select.closest('fieldset').dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue) {
                        select.value = optionValue;
                    }
                }
            });
            
            this.inputs.forEach((input) => {
                const fieldset = input.closest('fieldset');
                const optionPosition = fieldset.dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue && input.value === optionValue) {
                        input.checked = true;
                    }
                }
            });
            
            this.onChange();
        }
    }

    onChange() {
        this.updateOptions();
        this.updateDataId();
        this.updateOptionsNames();
        this.updateVariantStatuses();
        this.updateImages();

        // Trigger price recomputation on parent
        const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        if (deployerQuantityBreaks) {
            const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
            if (selectedQuantityBreak) {
                deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                deployerQuantityBreaks.updateAddButtonState();
                
                // Update form inputs with new variant
                deployerQuantityBreaks.updateFormInputs(selectedQuantityBreak);
            }
        }

        this.dispatchEvent(new CustomEvent('variantChange', {
            detail: {
                variant: this.currentVariant
            }
        }));
    }

    updateImages() {
        if (!this.currentVariant) return;
        
        const variant = this.variantData.find((v) => v.id === this.currentVariant.id);
        if (!variant) return;

        let imageUrl = null;

        // Check for transparent image metafield first
        const transparentImage = this.getTransparentImage(this.currentVariant.id);
        
        if (transparentImage) {
            imageUrl = transparentImage;
        } else if (variant.featured_image?.src) {
            imageUrl = variant.featured_image.src;
        }

        if (imageUrl) {
            this.setAdditionalProductImage(imageUrl);
        }
    }

    getTransparentImage(variantId) {
        const quantityBreak = this.closest('.quantity-break');
        const quantityBreakIndex = Array.from(document.querySelectorAll('.quantity-break')).indexOf(quantityBreak) + 1;
        const transparentImagesForProduct = additionalTransparentImages[quantityBreakIndex.toString()] || [];
        const transparentImage = transparentImagesForProduct.find((variant) => variant.id === variantId);
        return transparentImage?.transparentImage;
    }

    setAdditionalProductImage(imageUrl) {
        const quantityBreakMainContent = this.closest('.quantity-break_main-content');
        if (!quantityBreakMainContent) return;

        // Find all additional product images (those with the data attribute)
        const additionalImages = quantityBreakMainContent.querySelectorAll('img[data-additional-product="true"]');
        
        // Update all additional product images with the new variant image
        additionalImages.forEach((img) => {
            img.src = imageUrl;
        });
    }

    updateOptions() {
        this.options = [];
        const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
        fieldsets.sort((a, b) => {
            const posA = parseInt(a.dataset.optionPosition, 10) || 0;
            const posB = parseInt(b.dataset.optionPosition, 10) || 0;
            return posA - posB;
        });
        const maxPosition = fieldsets.reduce((max, fs) => {
            const p = parseInt(fs.dataset.optionPosition, 10) || 0;
            return Math.max(max, p);
        }, 0);
        for (let pos = 1; pos <= maxPosition; pos++) {
            const fieldset = fieldsets.find((fs) => parseInt(fs.dataset.optionPosition, 10) === pos);
            if (!fieldset) continue;
            const selectEl = fieldset.querySelector('select');
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (selectEl) {
                this.options.push(selectEl.value);
            } else if (checkedRadio) {
                this.options.push(checkedRadio.value);
            }
        }
    }

    updateDataId() {
        const matched = this.variantData.find((variant) => {
            if (!variant.options || variant.options.length !== this.options.length) return false;
            return !variant.options
                .map((option, index) => {
                    return this.options[index] === option;
                })
                .includes(false);
        });
        if (matched) {
            this.currentVariant = matched;
        } else {
            const fallback = this.variantData.find((v) => String(v.id) === String(this.dataset.id));
            if (fallback) this.currentVariant = fallback;
        }
        if (!this.currentVariant) return;

        this.dataset.id = String(this.currentVariant.id);
        this.dataset.available = deployerVariantIsPurchasable(this.currentVariant) ? 'true' : 'false';
    }

    updateVariantStatuses() {
        const selectedOptions = this.querySelectorAll(':checked');
        const inputWrappers = [...this.querySelectorAll('fieldset')];
        let selectedOptionOneVariants = null;
        let selectedOptionTwoVariants = null;
        let selectedOptionThreeVariants = null;
        if (selectedOptions[0]) {
            selectedOptionOneVariants = this.variantData.filter((variant) => selectedOptions[0].value === variant.options[0]);
        }
        if (selectedOptions[1]) {
            selectedOptionTwoVariants = this.variantData.filter((variant) => selectedOptions[1].value === variant.options[1]);
        }
        if (selectedOptions[2]) {
            selectedOptionThreeVariants = this.variantData.filter((variant) => selectedOptions[2].value === variant.options[2]);
        }
        inputWrappers.forEach((option, index) => {
            if (selectedOptions.length <= 1) return;
            if (index === 0) {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index + 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionTwoVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index + 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            } else {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index - 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            }
        });
    }

    setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
            const value = element.getAttribute('value');
            const availableElement = availableValuesList.includes(value);

            if (element.tagName === 'INPUT') {
                if (availableElement) {
                    element.classList.remove('disabled');
                } else {
                    element.classList.add('disabled');
                }
            } else if (element.tagName === 'OPTION') {
                if (availableElement) {
                    //element.disabled = false;
                    element.classList.remove('disabled');
                } else {
                    //element.disabled = true;
                    element.classList.add('disabled');
                }
            }
        });
    }

    updateOptionsNames() {
        this.optionsNames.forEach((optionName, index) => {
            optionName.innerText = this.options[index];
        });
    }
}
customElements.define('deployer-quantity-break-additional-variant-picker', QuantityBreakAdditionalVariantPicker);
